import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Key, UserCheck, Lock, Check, Sparkles } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem('saved_email');
    const savedPassword = localStorage.getItem('saved_password');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const response = await api.post('/auth/login', { email: cleanEmail, password });
      login(response.data);
      if (rememberMe) {
        localStorage.setItem('saved_email', cleanEmail);
        localStorage.setItem('saved_password', password);
      } else {
        localStorage.removeItem('saved_email');
        localStorage.removeItem('saved_password');
      }
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Invalid email or password. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFill = (emailVal: string, passVal: string) => {
    setEmail(emailVal);
    setPassword(passVal);
    setError('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#090d16', color: '#e2e8f0', fontFamily: "'Inter', sans-serif" }}>
      {/* HEADER */}
      <header style={{ padding: '0.85rem 1rem', backgroundColor: '#0b1120', borderBottom: '2px solid #ecc94b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <Link to="/" style={{ color: 'white', textDecoration: 'none', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img src="/logo_ELum_Firm4.png" alt="Logo" style={{ height: '30px', width: '30px', borderRadius: '50%', border: '1px solid #ecc94b' }} />
          <span>Elum Law Associates</span>
        </Link>
        <Link to="/" style={{ color: '#ecc94b', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 'bold' }}>
          ← Back to Main Portal
        </Link>
      </header>

      {/* LOGIN BOX CONTAINER */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1.25rem 0.75rem' }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          
          <form onSubmit={handleSubmit} style={{ padding: '1.75rem 1.25rem', backgroundColor: '#0f172a', borderRadius: '16px', border: '1px solid rgba(236,201,75,0.3)', boxShadow: '0 20px 50px rgba(0,0,0,0.8), 0 0 25px rgba(236,201,75,0.15)', boxSizing: 'border-box', width: '100%' }}>
            
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'inline-flex', padding: '0.65rem', borderRadius: '50%', backgroundColor: 'rgba(236,201,75,0.12)', color: '#ecc94b', marginBottom: '0.65rem', border: '1px solid rgba(236,201,75,0.3)' }}>
                <Lock size={26} />
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#ecc94b', margin: 0, lineHeight: '1.3' }}>
                Welcome to Pakistan's AI Powered Elum Legal Services & Law Directory
              </h2>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '6px', display: 'block', fontWeight: '600' }}>
                Portal Sign In — Secure Access for Verified System Roles & Clients
              </span>
            </div>

            {error && (
              <div style={{ backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444', color: '#fca5a5', padding: '0.75rem 0.9rem', borderRadius: '8px', fontSize: '0.82rem', textAlign: 'center', marginBottom: '1.25rem', fontWeight: '600', wordBreak: 'break-word' }}>
                ⚠️ {error}
              </div>
            )}

            {/* EMAIL INPUT */}
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.83rem', color: '#cbd5e1', fontWeight: '600' }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. superuser@elumlaw.com or admin@elumlaw.com"
                required
                style={{ width: '100%', padding: '0.75rem 0.9rem', backgroundColor: '#090d16', border: '1px solid #334155', borderRadius: '8px', color: 'white', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* PASSWORD INPUT */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.83rem', color: '#cbd5e1', fontWeight: '600' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password (e.g. Superuser@123)"
                required
                style={{ width: '100%', padding: '0.75rem 0.9rem', backgroundColor: '#090d16', border: '1px solid #334155', borderRadius: '8px', color: 'white', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* SAVE USER ID CHECKBOX */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: '#94a3b8', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ marginRight: '0.4rem', accentColor: '#ecc94b', width: '16px', height: '16px' }}
                />
                Remember user ID & password
              </label>

              <button
                type="button"
                onClick={() => { setEmail(''); setPassword(''); localStorage.removeItem('saved_email'); localStorage.removeItem('saved_password'); setRememberMe(false); }}
                style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear Fields
              </button>
            </div>

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.85rem',
                backgroundColor: '#ecc94b',
                color: '#090d16',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '800',
                cursor: loading ? 'wait' : 'pointer',
                boxShadow: '0 4px 15px rgba(236,201,75,0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? 'Authenticating...' : 'Sign In to Portal'}
            </button>

            <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.83rem', color: '#94a3b8', margin: '1.5rem 0 0' }}>
              Don't have a verified account?{' '}
              <Link to="/register" style={{ color: '#ecc94b', fontWeight: 'bold', textDecoration: 'none' }}>
                Register Account
              </Link>
            </p>
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
